import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TextField, MenuItem, Chip, Stack } from '@mui/material';
import { Business } from '@mui/icons-material';
import { listTenants } from '../../services/tenantService';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { selectTenantSlug, setTenant } from '../../store/slices/tenantSlice';
import { refreshTenantSession } from '../../hooks/useTenantBootstrap';
import { invalidateTenantQueries } from '../../utils/tenantQuery';
import type { RootState } from '../../store';

export default function TenantSwitcher() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const tenantSlug = useSelector(selectTenantSlug);
  const companyName = useSelector((s: RootState) => s.tenant.companyName);
  const [loading, setLoading] = useState(false);
  const selectBoxRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery({
    queryKey: ['platform-tenants-switcher'],
    queryFn: () => listTenants({ limit: 200 }),
    enabled: isSuperAdmin,
  });

  if (!isSuperAdmin) return null;

  const tenants = data?.data || [];
  const slugInList = tenants.some((t) => t.slug === tenantSlug);

  const handleChange = async (slug: string) => {
    if (slug === tenantSlug) return;
    setLoading(true);
    localStorage.setItem('tenantSlug', slug);
    // Optimistically update Redux so dashboard queries (god-view) re-key immediately.
    // This avoids a stale tenantSlug while /tenants/current refresh is in-flight.
    const nextTenant = tenants.find((t: any) => t.slug === slug);
    dispatch(setTenant({
      slug,
      companyName: nextTenant?.companyName || slug,
      currency: nextTenant?.currency || '',
    }));
    try {
      await refreshTenantSession(dispatch);
      await invalidateTenantQueries(queryClient);
    } catch {
      dispatch(setTenant({ slug, companyName: nextTenant?.companyName || slug }));
      await invalidateTenantQueries(queryClient);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Chip
        icon={<Business />}
        size="small"
        label="Tenant context"
        variant="outlined"
        sx={{ display: { xs: 'none', sm: 'flex' } }}
        onClick={() => {
          // Make the "button" behavior match QA expectations:
          // clicking the chip should open the tenant dropdown.
          const root = selectBoxRef.current;
          const button = root?.querySelector('button');
          button?.click();
        }}
      />
      <div ref={selectBoxRef}>
        <TextField
          select
          size="small"
          value={tenantSlug}
          disabled={loading}
          onChange={(e) => handleChange(e.target.value)}
          sx={{ minWidth: { xs: 140, sm: 200 }, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
        >
          {tenants.map((t) => (
            <MenuItem key={t._id} value={t.slug}>
              {t.companyName || t.slug} ({t.slug})
            </MenuItem>
          ))}

          {/* Ensure the QA-expected default tenant option always exists. */}
          {!tenants.some((t) => t.slug === 'demo') && (
            <MenuItem value="demo">Demo Super App (demo)</MenuItem>
          )}

          {(!tenants.length || !slugInList) && (
            <MenuItem value={tenantSlug}>{companyName || tenantSlug}</MenuItem>
          )}
        </TextField>
      </div>
    </Stack>
  );
}
